// weightCalc.ts — CNC/단조 세부원가 "투입중량 계산" + "제품중량 계산" 팝업의 순수 계산.
//
// 프론트 전용 헬퍼(백엔드 미러 없음). 단위: 길이 mm, 단중 g/mm³, 중량 g.
//
// 원주율: 제품중량 계산표는 고객 스펙("원주율 고정값")대로 3.142(CIRCLE_PI), 투입중량(1본 중량)은
// 내용추가18 고객 확정대로 3.14(INPUT_PI). (정미중량 netweight.ts 의 Math.PI / 봉재 material.ts 와 별개.)

import { CUT_WIDTH_STEPS } from "@/lib/cost/material";

// biome-ignore lint/suspicious/noApproximativeNumericConstant: 고객 스펙상 원주율을 3.142 로 고정(Math.PI 아님 — 제품중량 계산표 명시값)
export const CIRCLE_PI = 3.142; // 제품중량 계산표 원주율 고정값(스펙)

// 정육각형 면적 계수: 외접원(꼭짓점까지) 반지름 R 기준 면적 = (3√3/2)·R².
const HEX_AREA_FACTOR = (3 * Math.sqrt(3)) / 2;

// ----- 절단 LOSS (외경 기준) -----

// 외경(mm) → 절단 LOSS(mm). material.ts 의 구간표(Ø≤4→1.5 … Ø≤30→2.5)를 재사용하되,
// 표 최대(30)를 초과하면 마지막 구간값으로 CLAMP 한다(throw 하지 않음 — 팝업 입력 보호).
export function cutLossForOuter(outerD: number): number {
  for (const [upper, loss] of CUT_WIDTH_STEPS) {
    if (outerD <= upper) return loss;
  }
  const last = CUT_WIDTH_STEPS[CUT_WIDTH_STEPS.length - 1];
  return last[1];
}

// ----- 투입중량 계산 (원자재 1본 → 잔봉 반영, 내용추가18) -----
//
// 원자재 1본(전장)의 중량을 그 1본에서 나오는 제품 수(1본 생산량)로 나눠, 잔봉(끝단 미사용분)
// 까지 투입중량에 반영한다. 고객 확정(내용추가18):
//   절단 길이   = 제품 길이 + 절단 LOSS
//   유효 길이   = 원자재 전장 − 잔봉 길이
//   1본 중량    = (외경 ÷ 2)² × 3.14 × 단중 × 원자재 전장      ← 원주율 3.14 (INPUT_PI)
//   1본 생산량  = 유효 길이 ÷ 절단 길이 → 소수점 절삭(정수)
//   1본 자재원가 = 원자재 원가(원/kg) × 1본 중량(g) ÷ 1,000   (참고 표시)
//   투입중량    = 1본 중량 ÷ 1본 생산량 — 팝업은 정밀값(9.6909) 표시,
//                메인 화면에는 소수 둘째 자리 버림값(9.69)을 저장한다.
// 중간값은 반올림하지 않는다(정밀도 유지 — 1본 중량 988 로 먼저 자르면 9.68 이 됨).

// 투입중량(1본 중량) 원주율 — 고객 확정 3.14. 제품중량 계산표(CIRCLE_PI 3.142)와는 별개.
export const INPUT_PI = 3.14;
export const DEFAULT_STOCK_LENGTH = 2500; // 원자재 전장 기본값 (mm) — 수정 가능
export const DEFAULT_REMNANT_LENGTH = 250; // 잔봉 길이 기본값 (mm) — 수정 가능

// 부동소수 잡음 보정 — 9.69 × 100 = 968.9999… 같은 값이 한 자리 내려가지 않게.
const FLOOR_EPS = 1e-9;

// 소수 digits 자리까지 버림(절삭). 예: truncateDecimals(9.6909, 2) = 9.69.
export function truncateDecimals(n: number, digits = 2): number {
  if (!Number.isFinite(n)) return 0;
  const f = 10 ** digits;
  return Math.floor(n * f + FLOOR_EPS) / f;
}

export interface InputWeightParams {
  outerD: number; // 외경 (mm)
  productLength: number; // 제품 길이 (mm)
  gPerMm3: number; // 단중 (g/mm³) — 재질 마스터에서 조회
  cutLoss?: number; // 절단 LOSS (mm) — 미지정 시 외경으로 자동 조회
  stockLength: number; // 원자재 전장 (mm)
  remnantLength: number; // 잔봉 길이 (mm)
  rawPricePerKg?: number; // 원자재 원가 (원/kg) — 1본 자재원가 표시용
}

export interface InputWeightResult {
  cutLoss: number; // 절단 LOSS (mm)
  cutLength: number; // 절단 길이 = 제품 길이 + 절단 LOSS
  effectiveLength: number; // 유효 길이 = 전장 − 잔봉
  barWeightG: number; // 1본 중량(g) = (외경/2)² × 3.14 × 단중 × 전장
  piecesPerBar: number; // 1본 생산량(개/본) = ⌊유효 길이 ÷ 절단 길이⌋ (0 = 생산 불가)
  barCost: number; // 1본 자재원가(원/본) = 원자재 원가(원/kg) × 1본 중량 ÷ 1000
  weightG: number; // 투입중량(g) 정밀값 = 1본 중량 ÷ 1본 생산량 (생산 불가면 0)
  appliedWeightG: number; // 메인 화면 저장값 = 투입중량 소수 둘째 자리 버림
}

// 투입중량(g)을 산출한다. (환봉 원자재 1본 기준 — 잔봉 반영)
export function inputWeight(p: InputWeightParams): InputWeightResult {
  const cutLoss = p.cutLoss ?? cutLossForOuter(p.outerD);
  const cutLength = p.productLength + cutLoss;
  const effectiveLength = p.stockLength - p.remnantLength;
  const radius = p.outerD / 2;
  const barWeightG = radius * radius * INPUT_PI * p.gPerMm3 * p.stockLength;
  const piecesPerBar =
    cutLength > 0 && effectiveLength > 0 ? Math.floor(effectiveLength / cutLength + FLOOR_EPS) : 0;
  const barCost = ((p.rawPricePerKg ?? 0) * barWeightG) / 1000;
  const weightG = piecesPerBar > 0 ? barWeightG / piecesPerBar : 0;
  return {
    cutLoss,
    cutLength,
    effectiveLength,
    barWeightG,
    piecesPerBar,
    barCost,
    weightG,
    appliedWeightG: truncateDecimals(weightG, 2),
  };
}

// ----- 제품중량 계산표 (형태별) -----

export type ProductShape = "round" | "hex" | "rect";

export interface ProductWeightRow {
  shape: ProductShape; // 원형 / 육각 / 사각
  outer: number; // 외경·가로 (mm)
  inner: number; // 내경 (mm) — 없으면 0
  length: number; // 길이 (mm)
  gPerMm3: number; // 단중 (g/mm³)
  qty: number; // 수량 (EA)
}

export interface ProductRowWeightResult {
  area: number; // 단면적 (mm²) — 내경 제외
  unitWeight: number; // 단품 중량(g) = 단면적 × 길이 × 단중
  totalWeight: number; // 총 중량(g) = 단품 중량 × 수량
}

// 형태별 단면적(mm²) — 내경이 있으면 내경분을 제외한다.
//   원형: π(외경/2)² − π(내경/2)²
//   육각: (3√3/2)(외경/2)² − (3√3/2)(내경/2)²   (외경/내경을 외접/내접원 지름으로 간주)
//   사각: (외경/가로)² − (내경)²                (정사각 가정 — 가로=세로)
function shapeArea(shape: ProductShape, outer: number, inner: number): number {
  const ro = outer / 2;
  const ri = inner / 2;
  if (shape === "round") return CIRCLE_PI * ro * ro - CIRCLE_PI * ri * ri;
  if (shape === "hex") return HEX_AREA_FACTOR * ro * ro - HEX_AREA_FACTOR * ri * ri;
  return outer * outer - inner * inner; // rect (정사각)
}

// 제품중량 계산표 한 행의 단품/총 중량(g).
export function productRowWeight(row: ProductWeightRow): ProductRowWeightResult {
  const area = shapeArea(row.shape, row.outer, row.inner);
  const unitWeight = area * row.length * row.gPerMm3;
  const totalWeight = unitWeight * row.qty;
  return { area, unitWeight, totalWeight };
}

// 표 전체 합계 중량(g) = Σ 행별 총 중량.
export function productTotalWeight(rows: ProductWeightRow[]): number {
  return rows.reduce((acc, r) => acc + productRowWeight(r).totalWeight, 0);
}

// ----- 재질정보 연동 (투입중량 → 제품중량) -----
//
// 투입중량 계산에서 고른 재질의 단중을 제품중량 계산 행이 그대로 따르게 한다.
// 행에 사용자가 단중을 직접 입력(override)했으면 그 값을, 비어 있으면 선택한
// 재질의 단중을 쓴다. 즉 "투입중량 재질 선택 → 제품중량 자동 연동"의 단일 진실 원천.
//
//   rowGPerMm3       행에 입력된 단중(g/mm³). 0 이하/NaN 이면 미입력(=override 없음).
//   materialGPerMm3  현재 선택된 재질의 단중(g/mm³).
export function effectiveRowDensity(rowGPerMm3: number, materialGPerMm3: number): number {
  return rowGPerMm3 > 0 ? rowGPerMm3 : materialGPerMm3;
}

// ----- 행별 규격명(재질) → 단중 연동 (issue #90) -----
//
// 제품중량 계산표의 규격명(spec)이 재질 마스터의 이름(name)이 되도록 바뀌었다.
// 행의 단중은 더 이상 단일 투입중량 재질에 종속되지 않고, "그 행의 규격명"으로
// 재질 마스터를 조회한 단중을 따른다(행별 종속). 우선순위:
//   1) 행에 단중을 직접 입력(override) → 그 값.
//   2) 규격명이 재질 마스터의 name 과 일치 → 그 재질의 단중.
//   3) 둘 다 아님(레거시 자유 텍스트/빈 규격명) → fallback 단중(투입중량 재질).
//
// master.raw 전체 타입(CncRawMaterial)에 의존하지 않도록 필요한 필드만 받는다.
export interface RowDensitySource {
  name: string;
  gPerMm3: number;
}

// 규격명(spec)으로 재질 마스터를 조회해 단중(g/mm³)을 찾는다. 못 찾으면 undefined.
export function densityForSpec(
  spec: string,
  materials: readonly RowDensitySource[]
): number | undefined {
  const key = spec.trim();
  if (!key) return undefined;
  return materials.find((m) => m.name === key)?.gPerMm3;
}

// 한 행의 실효 단중(g/mm³)을 우선순위(override > 규격명 조회 > fallback)대로 해석한다.
//   rowGPerMm3   행에 입력된 단중. 0 이하/NaN 이면 미입력(override 없음).
//   spec         행의 규격명(재질 name). 매칭되면 그 재질 단중.
//   materials    재질 마스터(name·gPerMm3).
//   fallback     매칭 실패 시 단중(투입중량 재질의 단중) — 레거시/빈 규격명 보호.
export function resolveRowDensity(
  rowGPerMm3: number,
  spec: string,
  materials: readonly RowDensitySource[],
  fallback: number
): number {
  if (rowGPerMm3 > 0) return rowGPerMm3;
  const bySpec = densityForSpec(spec, materials);
  return bySpec ?? fallback;
}

// ----- 투입중량 기하 → 제품중량 첫 행 프리필 (issue #92) -----
//
// 제품중량 계산 첫 행은 규격명·단중이 투입중량 재질로 자동 연동(#90)되지만,
// 외경·길이가 비어 있어 제품중량이 0 으로 나온다. 투입중량 계산에 입력한 기하정보
// (외경 = 환봉 외경, 길이 = 제품 길이)를 첫 행의 외경·가로/길이로 프리필한다.
//
// 규칙:
//   1) 외경·길이가 비어 있는 경우에만 투입중량 값으로 채운다(사용자가 지운 값은 강제로
//      다시 채우지 않음 — #90 의 effectiveRow/resolve "빈 값일 때만" 패턴과 일관).
//   2) 이미 값이 있는 행은 그대로 둔다(저장된 행/수동 편집 보존).
//   3) 투입중량 외경·길이 자체가 비어 있으면 채울 게 없으므로 원본을 그대로 둔다.
//
// 외경·길이만 다루는 문자열 기반 부분 행(GeometryRow)으로 정의해 행 전체 타입에
// 의존하지 않는다(라운드트립 안전 — 다른 필드는 손대지 않음).
export interface GeometryRow {
  outer: string; // 외경·가로 (mm) — 빈 문자열이면 미입력
  length: string; // 길이 (mm) — 빈 문자열이면 미입력
}

// 문자열 기하값이 "비어 있는지" 판정 — 공백만 있거나 빈 문자열이면 미입력으로 본다.
const isBlankGeom = (s: string): boolean => s.trim() === "";

// 행의 외경·길이가 비어 있으면 투입중량 기하(문자열)로 채운 새 행을 반환한다.
// 비어 있지 않은 필드는 보존하고, 채울 소스값(outerD/length)도 비어 있으면 건드리지 않는다.
export function withDefaultGeometry<R extends GeometryRow>(
  row: R,
  outerD: string,
  length: string
): R {
  const outer = isBlankGeom(row.outer) && !isBlankGeom(outerD) ? outerD.trim() : row.outer;
  const len = isBlankGeom(row.length) && !isBlankGeom(length) ? length.trim() : row.length;
  if (outer === row.outer && len === row.length) return row;
  return { ...row, outer, length: len };
}
